/**
 * MANPOWER COVERAGE — demand vs supply for one day, per site.
 *
 * This is the calculation ORchestrate can make and a pure scheduling product cannot: we know the DEMAND
 * (rooms with cases, and the supervision ratio each site runs at) as well as the SUPPLY (who has a shift
 * that day, and where they are credentialed). It answers "how short am I on Thursday", months ahead.
 *
 * Kept pure and structurally typed so it is unit-tested and reusable: the Manpower view, a future
 * locums/coverage-gap report, and the board can all read one definition instead of three near-copies.
 *
 * APPROVED REQUESTS APPEAR IMMEDIATELY. Nothing here snapshots or caches. Supply is whatever `shifts`
 * the caller passes, and every caller queries shifts BY DATE — so the moment an approval writes a Shift
 * row for a day, the next read of that day includes it. There is no publish step between approval and
 * these numbers; the only deliberate gate in the product is the separate "export to bullpen".
 */

import { CaseWindow, caseWindow, DAY_MINUTES } from './caseConcurrency.ts';

export interface CoverageRoom { operatingRoomId: string; hospitalId?: string }
/**
 * A case. Times are OPTIONAL: without them this module can only answer "did this room run today",
 * which is a day SUM. With them it can also answer "how many ran at once", which is what actually
 * decides staffing. Callers that pass no times get peak === null rather than a wrong number.
 */
export interface CoverageCase {
  operatingRoomId: string;
  startTimeUtc?: string;
  endTimeUtc?: string;
}
export interface CoverageSite { hospitalId: string; name?: string; isChildrensHospital?: boolean }
/** A provider with a shift on the day. `order != null` = pecking order; null/undefined = shift work. */
export interface CoverageProvider {
  employeeId: string;
  employeeType: string;
  credentials?: string[];
  order?: number | null;
}

/** 1:3 at a pediatric site, 1:4 everywhere else — the same rule Symphony applies (siteSupervisionLimit). */
export const PEDIATRIC_CAP = 3;
export const ADULT_CAP = 4;
export const siteSupervisionCap = (site?: CoverageSite): number =>
  site?.isChildrensHospital ? PEDIATRIC_CAP : ADULT_CAP;

/**
 * STAFFING MODEL — chosen PER SITE, because it is a property of how that site practises, not of the day.
 *   md-only   an anesthesiologist alone in every room. No CRNA demand at all.
 *   act       the care-team model: a CRNA in each room, one MD supervising up to the site cap.
 *   crna-only CRNAs practise independently. A CRNA in every room and no MD demand.
 *
 * This replaces treating 1:4 as a FACT about every site. A ratio is only meaningful under `act`; at an
 * MD-only site the old code reported the site short by one CRNA for every room it ran.
 */
export type StaffingModel = 'md-only' | 'act' | 'crna-only';
export const STAFFING_MODELS: readonly StaffingModel[] = ['md-only', 'act', 'crna-only'];
export const DEFAULT_STAFFING_MODEL: StaffingModel = 'act';
export const STAFFING_MODEL_LABELS: Record<StaffingModel, string> = {
  'md-only': 'MD only',
  act: 'ACT model',
  'crna-only': 'CRNA only',
};

/** What one site needs, given its model. The ratio applies ONLY under `act`. */
export const needForModel = (
  roomsOpen: number,
  model: StaffingModel,
  cap: number,
): { crnasNeeded: number; doctorsNeeded: number } => {
  const rooms = Math.max(0, roomsOpen);
  if (model === 'md-only') return { crnasNeeded: 0, doctorsNeeded: rooms };
  if (model === 'crna-only') return { crnasNeeded: rooms, doctorsNeeded: 0 };
  return { crnasNeeded: rooms, doctorsNeeded: Math.ceil(rooms / Math.max(1, cap)) };
};

/** Sampling granularity for the peak scan — fine enough that a short case cannot slip between samples. */
const PEAK_SAMPLE_STEP_MIN = 5;

export interface PeakDemand {
  /** Most rooms running ACROSS ALL SITES at one instant, and the local minute it happens. */
  peakRooms: number;
  peakRoomsMin: number;
  /** Most CRNAs needed at one instant, each site counted under its own model. */
  crnasAtPeak: number;
  crnasAtPeakMin: number;
  doctorsAtPeak: number;
  doctorsAtPeakMin: number;
  /** Per-site peak concurrent rooms, for the detail rows. */
  bySitePeakRooms: Record<string, number>;
}

/**
 * CONCURRENT demand, which is the number that decides whether a day is runnable.
 *
 * `roomsOpen` is a day SUM: a room with any case counts once, so 9 CRNAs "needed today" might be 14
 * needed simultaneously at 10:00 and 3 by 16:00. A day sum can look comfortable while the morning is
 * underwater.
 *
 * Two things this gets right that the obvious implementation does not:
 *  - It is the PEAK OF THE SUM, not the sum of per-site peaks. Two sites whose busy hours do not
 *    overlap must not add their peaks together.
 *  - CRNA peak and MD peak are tracked SEPARATELY, because the staffing model is per site: an MD-only
 *    site adds doctors and no CRNAs, so the two curves can crest in different buckets.
 *
 * Returns null when no case carries usable times, so a caller falls back to the day sums instead of
 * being handed a confident zero.
 */
export const peakDemand = (
  rooms: CoverageRoom[],
  cases: CoverageCase[],
  sites: CoverageSite[],
  modelFor: (hospitalId: string) => StaffingModel = () => DEFAULT_STAFFING_MODEL,
  /** The local day ("YYYY-MM-DD") the cases were fetched for — see caseWindow. Omit only for undated tests. */
  day?: string,
): PeakDemand | null => {
  const siteOf = new Map<string, string | undefined>(rooms.map(r => [r.operatingRoomId, r.hospitalId]));
  const siteById = new Map<string, CoverageSite>(sites.map(x => [x.hospitalId, x]));
  const windows: CaseWindow[] = [];
  for (const c of cases) {
    const hid = siteOf.get(c.operatingRoomId);
    if (!hid) continue;
    const w = caseWindow(c.startTimeUtc ?? '', c.endTimeUtc ?? '', c.operatingRoomId, hid, day);
    if (w) windows.push(w);
  }
  if (windows.length === 0) return null;

  let peakRooms = 0, peakRoomsMin = 0;
  let crnasAtPeak = 0, crnasAtPeakMin = 0;
  let doctorsAtPeak = 0, doctorsAtPeakMin = 0;
  const bySitePeakRooms: Record<string, number> = {};

  for (let m = 0; m < DAY_MINUTES; m += PEAK_SAMPLE_STEP_MIN) {
    const liveRooms = new Map<string, Set<string>>();
    for (const w of windows) {
      if (w.startMin <= m && m < w.endMin) {
        let set = liveRooms.get(w.hospitalId);
        if (!set) { set = new Set<string>(); liveRooms.set(w.hospitalId, set); }
        set.add(w.roomId);
      }
    }
    let totalRooms = 0, crnas = 0, docs = 0;
    for (const [hid, set] of liveRooms) {
      const n = set.size;
      totalRooms += n;
      if (n > (bySitePeakRooms[hid] ?? 0)) bySitePeakRooms[hid] = n;
      const need = needForModel(n, modelFor(hid), siteSupervisionCap(siteById.get(hid)));
      crnas += need.crnasNeeded;
      docs += need.doctorsNeeded;
    }
    if (totalRooms > peakRooms) { peakRooms = totalRooms; peakRoomsMin = m; }
    if (crnas > crnasAtPeak) { crnasAtPeak = crnas; crnasAtPeakMin = m; }
    if (docs > doctorsAtPeak) { doctorsAtPeak = docs; doctorsAtPeakMin = m; }
  }
  return { peakRooms, peakRoomsMin, crnasAtPeak, crnasAtPeakMin, doctorsAtPeak, doctorsAtPeakMin, bySitePeakRooms };
};

const isCrna = (p: CoverageProvider) => p.employeeType === 'nurse';
const isDoctor = (p: CoverageProvider) => p.employeeType === 'doctor';

/**
 * Credentials are stored as hospital NAMES on older records and hospitalIds on migrated ones — the same
 * split `utils/credential-gate.ts` canonicalises on the backend. An EMPTY list means eligible everywhere.
 */
export const siteEligibility = (sites: CoverageSite[]) => {
  const byName = new Map<string, string>();
  for (const s of sites) if (s.name) byName.set(s.name.trim().toLowerCase(), s.hospitalId);
  const canon = (c: string) => byName.get(String(c).trim().toLowerCase()) ?? c;
  return (p: CoverageProvider, hospitalId: string): boolean => {
    const creds = p.credentials ?? [];
    if (creds.length === 0) return true;
    for (const c of creds) if (canon(c) === hospitalId) return true;
    return false;
  };
};

/**
 * PRACTICE MODEL — how a client staffs a room.
 *   care-team: a CRNA in the room with an anesthesiologist supervising up to `ratio` rooms (4 adult, 3 pediatric)
 *   crna-only: CRNAs practise independently; a room needs one provider, no supervisor
 */
export type PracticeModel = { kind: 'care-team'; ratio?: number } | { kind: 'crna-only' };

export interface ProviderPlan {
  rooms: number;
  crnasAvailable: number;
  doctorsAvailable: number;
  ratio: number;
  /** Rooms a CRNA can actually run — capped by CRNAs, by rooms, AND by the supervisors available. */
  crnaRoomsStaffed: number;
  supervisorsUsed: number;
  /** Bare rooms a leftover doctor runs solo. */
  soloDoctorRooms: number;
  roomsStaffed: number;
  /** Rooms nobody can staff. Each needs one more provider. */
  providersShort: number;
  /** Braden's stated preference — a CRNA in every room: fill the gap with CRNAs, then how many more doctors that needs. */
  ifFilledWithCrnas: { crnas: number; extraDoctors: number };
}

/**
 * Ballpark provider shortfall for a day, with DOCTORS AND CRNAS SUBSTITUTING for each other — the thing
 * two independent "CRNAs short / MDs short" numbers cannot express (Braden 2026-10-04).
 *
 * Worked example, 137 rooms / 125 CRNAs / 40 docs at 1:4: the CRNAs take 125 rooms, which needs
 * ceil(125/4) = 32 supervisors, leaving 8 doctors for the 12 bare rooms -> 4 rooms unstaffed. Fill those
 * 4 with CRNAs instead and it becomes 129 CRNA rooms needing 33 supervisors plus 8 bare rooms = 41
 * doctors against 40 -> 1 doctor still needed.
 *
 * A BALLPARK, not a plan. A uniform ratio ignores that a pediatric site runs 1:3, and it says nothing
 * about credentials, shift windows or arrival times. The honest forecast is to run Symphony on the day;
 * this is the back-of-envelope figure for "roughly how short am I in three weeks".
 */
export const providerPlan = (
  rooms: number,
  crnasAvailable: number,
  doctorsAvailable: number,
  model: PracticeModel = { kind: 'care-team' },
): ProviderPlan => {
  const R = Math.max(0, Math.floor(rooms));
  const C = Math.max(0, Math.floor(crnasAvailable));
  const D = Math.max(0, Math.floor(doctorsAvailable));

  if (model.kind === 'crna-only') {
    const crnaRoomsStaffed = Math.min(C, R);
    const roomsStaffed = Math.min(R, C + D);
    return {
      rooms: R, crnasAvailable: C, doctorsAvailable: D, ratio: 1,
      crnaRoomsStaffed, supervisorsUsed: 0,
      soloDoctorRooms: roomsStaffed - crnaRoomsStaffed,
      roomsStaffed, providersShort: R - roomsStaffed,
      ifFilledWithCrnas: { crnas: R - roomsStaffed, extraDoctors: 0 },
    };
  }

  const k = Math.max(1, Math.floor(model.ratio ?? ADULT_CAP));
  const plan = (crnas: number, docs: number) => {
    const crnaRooms = Math.min(crnas, R, docs * k);     // supervisors cap how many CRNA rooms can run
    const supervisors = Math.ceil(crnaRooms / k);
    const solo = Math.min(R - crnaRooms, Math.max(0, docs - supervisors));
    return { crnaRooms, supervisors, solo, staffed: crnaRooms + solo };
  };
  const base = plan(C, D);
  const short = R - base.staffed;
  // Preferring a CRNA in every room: add `short` CRNAs, then see how many doctors that arrangement needs.
  const crnaRoomsThen = Math.min(C + short, R);
  const doctorsThen = Math.ceil(crnaRoomsThen / k) + (R - crnaRoomsThen);
  return {
    rooms: R, crnasAvailable: C, doctorsAvailable: D, ratio: k,
    crnaRoomsStaffed: base.crnaRooms,
    supervisorsUsed: base.supervisors,
    soloDoctorRooms: base.solo,
    roomsStaffed: base.staffed,
    providersShort: short,
    ifFilledWithCrnas: { crnas: short, extraDoctors: Math.max(0, doctorsThen - D) },
  };
};

export interface SiteDemand {
  hospitalId: string;
  /** Rooms with at least one case that day. */
  roomsOpen: number;
  /** One CRNA per open room (the supervised model this product plans to). */
  crnasNeeded: number;
  /** Supervisors for those rooms, collapsed at the site cap — NOT one per room. Zero under crna-only. */
  doctorsNeeded: number;
  /** The ACT ratio this site WOULD run at (3 pediatric / 4 adult). Only meaningful when model is 'act'. */
  cap: number;
  model: StaffingModel;
}

/**
 * Demand per site. Rooms are counted ONCE however many cases they hold, and a room with no case that day
 * is not demand at all. Doctors needed is ceil(rooms / cap): one anesthesiologist covers up to `cap`
 * CRNA rooms and cannot supervise across hospitals, so the ratio is applied per site.
 */
export const demandBySite = (
  rooms: CoverageRoom[],
  cases: CoverageCase[],
  sites: CoverageSite[],
  modelFor: (hospitalId: string) => StaffingModel = () => DEFAULT_STAFFING_MODEL,
): SiteDemand[] => {
  const siteOf = new Map<string, string | undefined>(rooms.map(r => [r.operatingRoomId, r.hospitalId]));
  const siteById = new Map<string, CoverageSite>(sites.map(s => [s.hospitalId, s]));
  const openRooms = new Map<string, Set<string>>();
  for (const c of cases) {
    const hid = siteOf.get(c.operatingRoomId);
    if (!hid) continue;                                    // a case in a room we do not know: not countable
    if (!openRooms.has(hid)) openRooms.set(hid, new Set());
    openRooms.get(hid)!.add(c.operatingRoomId);
  }
  const out: SiteDemand[] = [];
  for (const [hospitalId, set] of openRooms) {
    const cap = siteSupervisionCap(siteById.get(hospitalId));
    const roomsOpen = set.size;
    const model = modelFor(hospitalId);
    out.push({ hospitalId, roomsOpen, ...needForModel(roomsOpen, model, cap), cap, model });
  }
  return out.sort((a, b) => b.roomsOpen - a.roomsOpen || a.hospitalId.localeCompare(b.hospitalId));
};

export interface SiteSupply {
  hospitalId: string;
  crnas: string[];
  doctors: string[];
  crnaPecking: number;
  crnaShiftWork: number;
  doctorPecking: number;
  doctorShiftWork: number;
}

/** Who is ELIGIBLE at each site — a provider credentialed only at Uptown is not supply for Children's. */
export const supplyBySite = (
  providers: CoverageProvider[],
  sites: CoverageSite[],
  hospitalIds: string[],
): SiteSupply[] => {
  const eligible = siteEligibility(sites);
  return hospitalIds.map(hospitalId => {
    const here = providers.filter(p => eligible(p, hospitalId));
    const crnas = here.filter(isCrna);
    const doctors = here.filter(isDoctor);
    const pecking = (p: CoverageProvider) => p.order !== undefined && p.order !== null;
    return {
      hospitalId,
      crnas: crnas.map(p => p.employeeId),
      doctors: doctors.map(p => p.employeeId),
      crnaPecking: crnas.filter(pecking).length,
      crnaShiftWork: crnas.length - crnas.filter(pecking).length,
      doctorPecking: doctors.filter(pecking).length,
      doctorShiftWork: doctors.length - doctors.filter(pecking).length,
    };
  });
};

export interface SiteGap {
  hospitalId: string;
  roomsOpen: number;
  /** Peak CONCURRENT rooms at this site; undefined when cases carry no times. */
  peakRooms?: number;
  cap: number;
  model: StaffingModel;
  crnasNeeded: number;
  crnasEligible: number;
  doctorsNeeded: number;
  doctorsEligible: number;
  /** Shortfall judged against this site's ELIGIBLE pool alone, ignoring competition from other sites. */
  crnaShortLocal: number;
  doctorShortLocal: number;
}

export interface DayCoverage {
  sites: SiteGap[];
  crnasNeeded: number;
  doctorsNeeded: number;
  /** Distinct providers with a shift who are eligible at ≥1 site that has demand. */
  crnasUsable: number;
  doctorsUsable: number;
  /**
   * Plain distinct headcount on shift that day, by type, IGNORING credentials — the number you get by
   * counting the bullpen, which is why the total row compares against it. `crnasUsable` is the stricter
   * credential-aware figure; when the two differ, somebody on shift is not credentialed anywhere there
   * is demand, and `crnaShort`/`doctorShort` is the number that accounts for it.
   */
  crnasOnShift: number;
  doctorsOnShift: number;
  /** Concurrent demand, or null when no case carried usable times. */
  peak: PeakDemand | null;
  /**
   * Day shortfall from a GREEDY assignment that fills the tightest-credentialed site first — the same
   * ordering Symphony itself uses ("tightest credentialing lists first"). This is an estimate that
   * mirrors how the allocator behaves, NOT a proven-optimal matching: a different order could in
   * principle cover one more room. Treat it as the realistic gap, and the per-site numbers as the
   * actionable ones.
   */
  crnaShort: number;
  doctorShort: number;
}

/**
 * Greedily fill demand from eligible pools, tightest-credentialed site first, so a provider usable at
 * only one site is spent there rather than being counted twice.
 *
 * Summing per-site shortfalls OVERSTATES the day gap (shared providers get double-counted); comparing
 * bare totals UNDERSTATES it (a CRNA credentialed only at Uptown cannot cover Children's). This is why
 * the greedy pass exists at all.
 */
const greedyShort = (
  need: Map<string, number>,
  eligibleBySite: Map<string, string[]>,
): number => {
  const taken = new Set<string>();
  let short = 0;
  const order = [...need.keys()].sort((a, b) =>
    (eligibleBySite.get(a)?.length ?? 0) - (eligibleBySite.get(b)?.length ?? 0) || a.localeCompare(b));
  for (const hid of order) {
    let remaining = need.get(hid) ?? 0;
    for (const id of eligibleBySite.get(hid) ?? []) {
      if (remaining === 0) break;
      if (taken.has(id)) continue;
      taken.add(id);
      remaining--;
    }
    short += remaining;
  }
  return short;
};

/** The whole picture for one day. `providers` are those WITH A SHIFT that day (supply). */
export const dayCoverage = (
  rooms: CoverageRoom[],
  cases: CoverageCase[],
  sites: CoverageSite[],
  providers: CoverageProvider[],
  modelFor: (hospitalId: string) => StaffingModel = () => DEFAULT_STAFFING_MODEL,
  /** The local day ("YYYY-MM-DD") the cases were fetched for, so the peak scan anchors overnight cases. */
  day?: string,
): DayCoverage => {
  const demand = demandBySite(rooms, cases, sites, modelFor);
  const peak = peakDemand(rooms, cases, sites, modelFor, day);
  const hospitalIds = demand.map(d => d.hospitalId);
  const supply = supplyBySite(providers, sites, hospitalIds);
  const supplyById = new Map(supply.map(s => [s.hospitalId, s]));

  const siteGaps: SiteGap[] = demand.map(d => {
    const s = supplyById.get(d.hospitalId);
    const crnasEligible = s?.crnas.length ?? 0;
    const doctorsEligible = s?.doctors.length ?? 0;
    return {
      hospitalId: d.hospitalId,
      roomsOpen: d.roomsOpen,
      peakRooms: peak?.bySitePeakRooms[d.hospitalId],
      cap: d.cap,
      model: d.model,
      crnasNeeded: d.crnasNeeded,
      crnasEligible,
      doctorsNeeded: d.doctorsNeeded,
      doctorsEligible,
      crnaShortLocal: Math.max(0, d.crnasNeeded - crnasEligible),
      doctorShortLocal: Math.max(0, d.doctorsNeeded - doctorsEligible),
    };
  });

  const crnaNeed = new Map(demand.map(d => [d.hospitalId, d.crnasNeeded]));
  const docNeed = new Map(demand.map(d => [d.hospitalId, d.doctorsNeeded]));
  const crnaElig = new Map(supply.map(s => [s.hospitalId, s.crnas]));
  const docElig = new Map(supply.map(s => [s.hospitalId, s.doctors]));

  const usable = (pools: Map<string, string[]>) => {
    const set = new Set<string>();
    for (const ids of pools.values()) for (const id of ids) set.add(id);
    return set.size;
  };

  return {
    sites: siteGaps,
    crnasNeeded: demand.reduce((a, d) => a + d.crnasNeeded, 0),
    doctorsNeeded: demand.reduce((a, d) => a + d.doctorsNeeded, 0),
    crnasUsable: usable(crnaElig),
    doctorsUsable: usable(docElig),
    crnasOnShift: new Set(providers.filter(isCrna).map(p => p.employeeId)).size,
    doctorsOnShift: new Set(providers.filter(isDoctor).map(p => p.employeeId)).size,
    peak,
    crnaShort: greedyShort(crnaNeed, crnaElig),
    doctorShort: greedyShort(docNeed, docElig),
  };
};
